import { useCallback, useEffect } from "react";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { ProductCategory } from "@utils/constants";

/**
 * Module-level set tracking which productTypes have shown the toast in
 * the current screen focus window. Prevents double-firing when FilterScreen
 * and SearchResultScreen are both mounted (web split view).
 * Cleared on unmount so the next navigation visit can show again.
 */
const shownToastSet = new Set<string>();

interface UseLabGrownToastParams {
  productType: ProductCategory;
  /** Active tab/section key, e.g. routes[index]?.key or sectionName */
  activeTabKey?: string;
  getAppSettings: any;
  userInfo: any;
  t: (key: string) => string;
}

export const useLabGrownToast = ({
  productType,
  activeTabKey,
  getAppSettings,
  userInfo,
  t,
}: UseLabGrownToastParams) => {
  useEffect(() => {
    return () => {
      shownToastSet.delete(productType);
    };
  }, [productType]);

  /**
   * Shows the appropriate lab-grown toast once per focus window.
   * Returns `true` if the LAB_GROWN_DIAMOND popup should also be opened.
   */
  const triggerLabGrownToast = useCallback((): boolean => {
    if (shownToastSet.has(productType)) return false;

    if (
      productType === ProductCategory.LAB_GROWN_DIAMOND &&
      userInfo.showLabGrownPopup()
    ) {
      showToast("", userInfo.getLabGrownText(), ToastTypes.INFO, true, true);
      shownToastSet.add(productType);
      return true;
    }

    if (
      productType === ProductCategory.JEWELRY &&
      getAppSettings?.displayLabGrownJewelryToast &&
      activeTabKey === "Lab-Grown Jewelry"
    ) {
      showToast(
        "",
        t("global.toast.labgrownjewelry.title"),
        ToastTypes.LABGROWN_JEWELRY,
        true,
        true
      );
      shownToastSet.add(productType);
    }

    return false;
  }, [productType, activeTabKey, getAppSettings, userInfo, t]);

  /** Clears the dedup key on blur so the next focus event can show again. */
  const clearLabGrownToast = useCallback(() => {
    shownToastSet.delete(productType);
  }, [productType]);

  return { triggerLabGrownToast, clearLabGrownToast };
};
